/**
 * Fotos de internet para un producto (the owner, 2026-10-22: «a photo suggester that looks for
 * photos on the internet of that particular product as a suggested picture we can use for that
 * product's thumbnail»): what the shop's server found for it by its codes and its name, in the open
 * catalogues and in Wikipedia (`apps/api/src/routes/sugerencias.ts`), and each photo's bytes, which
 * come through the server too, as a page may load nothing from anywhere else.
 *
 * Never asked from the practice shop, which talks to no server. A copy of the shop asks, unlike the
 * catalogues' lookups of a code (`catalogos-abiertos.ts`): it has a server of its own, and it is
 * where this is tried on the shop's own products before it goes live, which is where the owner
 * first looked for it and found no button (2026-10-22).
 */

import { esGtin, limpiarCodigo } from '@abastece/shared';
import type { Producto, UnidadVenta } from '@abastece/core';
import { OfflineError, api } from '../sync/client.js';
import { enPractica } from './practica.js';

/** A photo the server found (`FotoSugerida` there). */
export interface FotoSugerida {
  id: string;
  /** Where, to credit: «Open Food Facts», «Wikipedia». */
  fuente: string;
  /** What it is there. */
  titulo: string;
  /** Its page there. */
  enlace: string;
  /** Found by one of the product's own codes: the very product. */
  exacta?: true;
}

export interface FotosSugeridas {
  fotos: FotoSugerida[];
  /** A place could not be asked just now: asked again in a minute, it may find more. */
  ocupado?: true;
}

/** Whether this device asks at all. */
export const sePuedenSugerir = (): boolean => !enPractica();

/** How many codes are sent: the product's and a few of its presentations'. */
const CODIGOS_MAX = 4;

/** The codes a catalogue could know the product by: its own first, then its presentations', GTINs only. */
export function codigosParaBuscar(producto: Pick<Producto, 'id' | 'codigosBarras'>, unidades: readonly UnidadVenta[]): string[] {
  const todos = [...producto.codigosBarras, ...unidades.filter((u) => u.productoId === producto.id).flatMap((u) => u.codigosBarras ?? [])];
  return [...new Set(todos.map(limpiarCodigo).filter(esGtin))].slice(0, CODIGOS_MAX);
}

export type Busqueda =
  | { estado: 'listo'; fotos: FotoSugerida[]; ocupado: boolean }
  /** No server to ask: no signal, or it is down. */
  | { estado: 'sinConexion' }
  | { estado: 'error' };

/** The searches on their way, so that a sheet drawn twice asks once. */
const enCurso = new Map<string, Promise<Busqueda>>();

/** What the server found for a product, by the shop's own name for it and its codes. */
export function buscarFotos(nombre: string, codigos: readonly string[]): Promise<Busqueda> {
  const clave = `${nombre}|${codigos.join(',')}`;
  let busqueda = enCurso.get(clave);
  if (!busqueda) {
    busqueda = preguntar(nombre, codigos).finally(() => enCurso.delete(clave));
    enCurso.set(clave, busqueda);
  }
  return busqueda;
}

async function preguntar(nombre: string, codigos: readonly string[]): Promise<Busqueda> {
  try {
    const r = await api.fotosSugeridas(nombre, codigos);
    return { estado: 'listo', fotos: r.fotos, ocupado: Boolean(r.ocupado) };
  } catch (e) {
    return e instanceof OfflineError ? { estado: 'sinConexion' } : { estado: 'error' };
  }
}

/** Each photo's bytes, asked once a page load; one that did not come is asked again next time. */
const traidas = new Map<string, Promise<Blob | null>>();

export function traerFotoSugerida(id: string): Promise<Blob | null> {
  let traida = traidas.get(id);
  if (!traida) {
    traida = api.fotoSugerida(id).catch(() => null);
    traidas.set(id, traida);
    void traida.then((b) => {
      if (!b) traidas.delete(id);
    });
  }
  return traida;
}
